import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Scale, Building2, Users, Briefcase, ShieldAlert, Landmark, FileText, Gavel, ArrowRight, CheckCircle2 } from 'lucide-react';
import { practiceAreas } from '../mockData';
import ParticleNetwork from '../components/ParticleNetwork';

const iconMap = {
  Scale: Scale,
  Building2: Building2,
  Users: Users,
  Briefcase: Briefcase,
  ShieldAlert: ShieldAlert,
  Landmark: Landmark,
  FileText: FileText,
  Gavel: Gavel
};

const Home = () => {
  return (
    <div>
      <Helmet>
        <title>D J Govindaraju and Associates | Advocates in Bengaluru - Seasoned in Dispute Resolution and Arbitration</title>
        <meta name="description" content="D J Govindaraju and Associates - Advocates and legal practitioners in Bengaluru, seasoned in dispute resolution and arbitration. Practising across Civil & Commercial Litigation, Real Estate, Family Law, Corporate Law, White Collar Crimes, Public Law, Transaction Advisory, and Arbitration and Conciliation for over 25 years." />
        <link rel="canonical" href="https://www.djgovindaraju.com/" />
      </Helmet>
      {/* Hero Section with Particle Network */}
      <section className="section-hero">
        <ParticleNetwork />
        <div className="container hero-content-wrapper" style={{ textAlign: 'center', maxWidth: '900px' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '2rem',
              padding: '0.5rem 1.25rem',
              marginBottom: '2rem',
              fontFamily: 'SF Mono, monospace',
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.025em',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              color: 'var(--text-light)'
            }}
          >
            <CheckCircle2 size={16} style={{ color: 'var(--accent-gold-light)' }} />
            Expertise Driven Legal Practice in Bengaluru for More Than 25 Years
          </div>

          <h1 className="heading-hero" style={{ marginBottom: '1.5rem', color: 'var(--text-light)', textShadow: '0 2px 12px rgba(0,0,0,0.4)' }}>
            D J Govindaraju and Associates
          </h1>
          <p className="body-large" style={{ marginBottom: '2.5rem', color: 'rgba(245, 239, 231, 0.95)', maxWidth: '700px', margin: '0 auto 2.5rem', textShadow: '0 1px 6px rgba(0,0,0,0.3)' }}>
            Delivering excellence in legal practice across Civil & Commercial Litigation, Real Estate, Family Law, Corporate matters, White Collar Crimes, Public Law, Transaction Advisory, and Arbitration and Conciliation.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn-primary" style={{ textDecoration: 'none' }}>
              Contact Us
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Practice Areas */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem', color: 'var(--text-light)' }}>
              Areas of Practice
            </h2>
            <p className="body-medium" style={{ color: 'rgba(245, 239, 231, 0.9)', maxWidth: '600px', margin: '0 auto' }}>
              Comprehensive legal expertise across diverse practice areas handling complex disputes.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem'
            }}
          >
            {practiceAreas.map((area) => {
              const IconComponent = iconMap[area.icon];
              return (
                <div key={area.id} className="card-expertise">
                  <div
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2), rgba(232, 201, 93, 0.3))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '1.5rem',
                      border: '2px solid rgba(212, 175, 55, 0.3)'
                    }}
                  >
                    <IconComponent size={28} style={{ color: 'var(--accent-gold-dark)' }} />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                    {area.title}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {area.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Approach to Litigation */}
      <section className="section" style={{ background: 'linear-gradient(180deg, var(--bg-tan-medium) 0%, var(--bg-tan-dark) 100%)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem', color: 'var(--text-light)' }}>
              Our Approach to Litigation
            </h2>
            <p className="body-medium" style={{ color: 'rgba(245, 239, 231, 0.9)', maxWidth: '700px', margin: '0 auto' }}>
              Our litigation methodology is built on strategic thinking, meticulous preparation, and unwavering commitment to client success.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem',
              maxWidth: '1100px',
              margin: '0 auto'
            }}
          >
            {[
              {
                title: 'Comprehensive Case Analysis',
                description: 'Thorough examination of facts, evidence, and legal precedents to build a robust foundation for every case'
              },
              {
                title: 'Strategic Planning',
                description: 'Developing tailored litigation strategies that align with client objectives and maximize chances of favorable outcomes'
              },
              {
                title: 'Meticulous Documentation',
                description: 'Precise drafting of pleadings, applications, and legal documents ensuring procedural compliance and substantive strength'
              },
              {
                title: 'Aggressive Yet Ethical Advocacy',
                description: 'Vigorous representation in court while maintaining the highest standards of professional ethics and integrity'
              },
              {
                title: 'Alternative Dispute Resolution',
                description: 'Exploring mediation, arbitration, and negotiated settlements when appropriate to achieve efficient resolutions'
              },
              {
                title: 'Client-Centric Communication',
                description: 'Regular updates, transparent discussions, and collaborative decision-making throughout the litigation process'
              }
            ].map((item, index) => (
              <div key={index} className="card" style={{ textAlign: 'left' }}>
                <div
                  style={{
                    width: '45px',
                    height: '45px',
                    borderRadius: '50%',
                    background: 'var(--accent-gold)',
                    color: 'var(--text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem',
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)'
                  }}
                >
                  {index + 1}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                  {item.title}
                </h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section" style={{ background: 'linear-gradient(180deg, var(--bg-tan-dark) 0%, var(--bg-darker) 100%)', color: 'white', textAlign: 'center', paddingBottom: '0' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem', color: 'var(--text-light)' }}>
            Contact Us
          </h2>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link
              to="/contact"
              className="btn-primary"
              style={{ textDecoration: 'none' }}
            >
              Details
              <ArrowRight size={16} />
            </Link>
            <a href="mailto:djg.advocate@gmail.com" className="btn-secondary" style={{ textDecoration: 'none', color: 'var(--text-light)', borderColor: 'rgba(212, 175, 55, 0.4)' }}>
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;